import { Icon } from '../../utils/icons';
import { useState } from 'react';

const WidgetTypeModal = ({ isOpen, onClose, onSelectType }) => {
    const [imageLoadStates, setImageLoadStates] = useState({});

    if (!isOpen) return null;

    const widgetTypes = [
        {
            id: 'review',
            name: 'Review Widget',
            description: 'Showcase customer reviews and testimonials to build trust and convert visitors into customers',
            image: 'https://d316l465hyuswd.cloudfront.net/filters:quality(80)/assets/images/rating-reviews-widgets.jpg',
            fallbackImage: '/review_widgets.gif',
            color: 'bg-blue-50',
            iconColor: 'text-blue-500',
            buttonColor: 'bg-blue-500',
            buttonHoverColor: 'hover:bg-blue-600',
            borderColor: 'hover:border-blue-200',
            textColor: 'group-hover:text-blue-600',
            features: ['Multiple layouts', 'Platform integration', 'Review filtering', 'Customizable styling']
        },
        {
            id: 'appointment',
            name: 'Appointment Booking Widget',
            description: 'Allow customers to book appointments directly through your website with automated scheduling',
            image: 'https://d316l465hyuswd.cloudfront.net/filters:quality(80)/assets/images/customer-complaints.jpg',
            fallbackImage: '/calendar_icon.svg',
            color: 'bg-purple-50',
            iconColor: 'text-purple-500',
            buttonColor: 'bg-purple-500',
            buttonHoverColor: 'hover:bg-purple-600',
            borderColor: 'hover:border-purple-200',
            textColor: 'group-hover:text-purple-600',
            features: ['Service management', 'Real-time availability', 'Email notifications', 'Calendar integration']
        },
        {
            id: 'whatsapp',
            name: 'WhatsApp Chat Widget',
            description: 'Add a floating WhatsApp chat button to connect with customers instantly and boost engagement',
            image: 'https://d316l465hyuswd.cloudfront.net/filters:quality(80)/assets/images/chatbot-widget.jpg',
            fallbackImage: '/whatsapp_icon.svg',
            color: 'bg-green-50',
            iconColor: 'text-green-500',
            buttonColor: 'bg-green-500',
            buttonHoverColor: 'hover:bg-green-600',
            borderColor: 'hover:border-green-200',
            textColor: 'group-hover:text-green-600',
            features: ['Direct messaging', 'Custom welcome text', 'Availability settings', 'Position control']
        }
    ];

    const handleTypeSelect = (type) => {
        onSelectType(type);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto border border-gray-100">
                {/* Header */}
                <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100">
                    <div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-1">Choose Widget Type</h2>
                        <p className="text-base text-gray-600">Select the widget that best fits your business needs</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl p-2 transition-all duration-200 hover:shadow-sm"
                    >
                        <Icon name="close" size={24} />
                    </button>
                </div>

                {/* Widget Types */}
                <div className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {widgetTypes.map((widget) => (
                            <div
                                key={widget.id}
                                onClick={() => handleTypeSelect(widget.id)}
                                className={`group border border-gray-200 ${widget.borderColor} rounded-xl cursor-pointer transition-all duration-300 hover:shadow-xl hover:shadow-gray-100 overflow-hidden bg-white hover:-translate-y-1`}
                            >
                                {/* Image Section */}
                                <div className="relative h-52 w-full bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
                                    {/* Loading state */}
                                    {!imageLoadStates[widget.id] && (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="animate-pulse">
                                                <div className={`${widget.color} border border-gray-200 w-16 h-16 rounded-2xl flex items-center justify-center mb-3 mx-auto`}>
                                                    {widget.id === 'review' ? (
                                                        <Icon name="star" size={32} className={widget.iconColor} />
                                                    ) : widget.id === 'appointment' ? (
                                                        <img
                                                            src="/calendar_icon.svg"
                                                            alt="Calendar"
                                                            className={`w-8 h-8 ${widget.iconColor}`}
                                                        />
                                                    ) : (
                                                        <img
                                                            src="/whatsapp_icon.svg"
                                                            alt="WhatsApp"
                                                            className={`w-8 h-8 ${widget.iconColor}`}
                                                        />
                                                    )}
                                                </div>
                                                <p className="text-sm font-medium text-gray-600 text-center">Loading preview...</p>
                                            </div>
                                        </div>
                                    )}

                                    <img
                                        src={widget.image}
                                        alt={`${widget.name} Preview`}
                                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                                        style={{ display: imageLoadStates[widget.id] === true ? 'block' : 'none' }}
                                        onLoad={() => {
                                            setImageLoadStates(prev => ({ ...prev, [widget.id]: true }));
                                        }}
                                        onError={(e) => {
                                            // Try fallback image
                                            if (e.target.src !== widget.fallbackImage) {
                                                e.target.src = widget.fallbackImage;
                                            } else {
                                                setImageLoadStates(prev => ({ ...prev, [widget.id]: 'error' }));
                                            }
                                        }}
                                    />

                                    {/* Error fallback */}
                                    {imageLoadStates[widget.id] === 'error' && (
                                        <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                                            <div className="text-center">
                                                <div className={`${widget.color} border border-gray-200 w-16 h-16 rounded-2xl flex items-center justify-center mb-3 mx-auto`}>
                                                    {widget.id === 'review' ? (
                                                        <Icon name="star" size={32} className={widget.iconColor} />
                                                    ) : widget.id === 'appointment' ? (
                                                        <img
                                                            src="/calendar_icon.svg"
                                                            alt="Calendar"
                                                            className={`w-8 h-8 ${widget.iconColor}`}
                                                        />
                                                    ) : (
                                                        <img
                                                            src="/whatsapp_icon.svg"
                                                            alt="WhatsApp"
                                                            className={`w-8 h-8 ${widget.iconColor}`}
                                                        />
                                                    )}
                                                </div>
                                                <p className="text-sm font-medium text-gray-700">{widget.name}</p>
                                                <p className="text-xs text-gray-500 mt-1">Preview Image</p>
                                            </div>
                                        </div>
                                    )}

                                    {imageLoadStates[widget.id] === true && (
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent"></div>
                                    )}
                                </div>

                                {/* Content Section */}
                                <div className="p-6 space-y-4">
                                    {/* Header */}
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <h3 className={`text-xl font-bold text-gray-900 ${widget.textColor} transition-colors mb-2`}>
                                                {widget.name}
                                            </h3>
                                            <p className="text-gray-600 text-sm leading-relaxed">
                                                {widget.description}
                                            </p>
                                        </div>
                                        <div className={`${widget.color} border border-gray-200 w-8 h-8 rounded-lg flex items-center justify-center ml-3 flex-shrink-0`}>
                                            {widget.id === 'review' ? (
                                                <Icon name="star" size={16} className={widget.iconColor} />
                                            ) : widget.id === 'appointment' ? (
                                                <img
                                                    src="/calendar_icon.svg"
                                                    alt="Calendar"
                                                    className={`w-4 h-4 ${widget.iconColor}`}
                                                />
                                            ) : (
                                                <img
                                                    src="/whatsapp_icon.svg"
                                                    alt="WhatsApp"
                                                    className={`w-4 h-4 ${widget.iconColor}`}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    {/* Features */}
                                    <div className="space-y-1">
                                        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Features</h4>
                                        <div className="flex flex-wrap gap-1.5">
                                            {widget.features.slice(0, 2).map((feature, index) => (
                                                <span key={index} className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-xs text-gray-600 font-medium">
                                                    <div className={`w-1.5 h-1.5 rounded-full mr-2 ${widget.iconColor === 'text-blue-500' ? 'bg-blue-400' : widget.iconColor === 'text-purple-500' ? 'bg-purple-400' : 'bg-green-400'}`}></div>
                                                    {feature}
                                                </span>
                                            ))}
                                            {widget.features.length > 2 && (
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-xs text-gray-500 font-medium">
                                                    +{widget.features.length - 2} more
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Action Button */}
                                    <div className="pt-2">
                                        <button className={`w-full ${widget.buttonColor} ${widget.buttonHoverColor} text-white py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center space-x-2 hover:shadow-lg shadow-sm`}>
                                            <span>Create Widget</span>
                                            <Icon name="add" size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-8 py-4 bg-gray-50/50 border-t border-gray-100 rounded-b-3xl">
                    <p className="text-sm text-gray-500 text-center font-medium">
                        💡 You can create multiple widgets or switch types anytime
                    </p>
                </div>
            </div>
        </div>
    );
};

export default WidgetTypeModal;
